import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import {
  authenticatedApiClient,
  throwErrorByStatus,
} from "../../../api/apiClients";
import { Input } from "../../../components/commons";
import { ObjectTypes, pathKeys } from "../../../constants";
import { ClientAccountContext } from "../../App";
import { useDebounce } from "../../../utils";

import "./style.scss";

const DocumentNumberField = (props) => {
  const {
    value,
    onChange,
    disabled,
    documentType,
    voucherId,
    businessPartnerId,
    tabIndex,
  } = props;

  const { t } = useTranslation();
  const { clientId } = useParams();
  const debouncedvalue = useDebounce(value);

  // Context state
  const clientAccount = useContext(ClientAccountContext);

  // Helper state
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateVoucherId, setDuplicateVoucherId] = useState();

  const checkExistingDocuments = (result) => {
    const duplicate = result?.documents?.find(
      (el) =>
        el.business_partner_id === businessPartnerId &&
        el.document_type === documentType &&
        el.document_number === value &&
        !el.files?.find((f) => f.type === "VOUCHER" && f.is_active === false) &&
        (!voucherId || el.file_id !== voucherId)
    );

    setShowDuplicateWarning(!!duplicate);
    setDuplicateVoucherId(duplicate?.file_id);
  };

  // Queries
  const { isLoading } = useQuery(
    [
      "documents",
      {
        client_account_id: clientAccount?.id,
        business_partner_id: businessPartnerId,
        document_type: documentType,
        document_number: debouncedvalue,
      },
    ],
    async () => {
      const res = await authenticatedApiClient.get("/documents", {
        params: {
          client_account_id: clientAccount?.id,
          business_partner_id: businessPartnerId,
          document_type: documentType,
          document_number: debouncedvalue,
        },
      });
      throwErrorByStatus(res);
      return res.data;
    },
    {
      enabled: debouncedvalue?.length > 2,
      meta: { ignoreLoadingIndicator: true },
      onSuccess: checkExistingDocuments,
    }
  );

  const onDuplicateLabelClicked = () => {
    if (duplicateVoucherId) {
      const win = window.open(
        `${pathKeys.CLIENT_ACCOUNTS}/${clientId}${pathKeys.POSTS}/${duplicateVoucherId}`,
        "_blank"
      );
      win.focus();
    }
  };

  const labelNames = {
    [ObjectTypes.AR_INVOICE]: t("post_detail.invoice_number"),
    [ObjectTypes.AR_CREDIT_NOTE]: t("post_detail.creditnote_number"),
    [ObjectTypes.AR_DEBT_COLLECTION]: t(
      "post_detail.debt_collection_document_number"
    ),
    [ObjectTypes.AP_INVOICE]: t("post_detail.invoice_number"),
    [ObjectTypes.AP_CREDIT_NOTE]: t("post_detail.creditnote_number"),
    [ObjectTypes.AP_RECEIPT]: t("post_detail.receipt_number"),
    [ObjectTypes.AP_DEBT_COLLECTION]: t(
      "post_detail.debt_collection_document_number"
    ),
  };

  const label = (
    <>
      {labelNames[documentType]}
      {showDuplicateWarning && (
        <a
          style={{
            position: "absolute",
            right: "0px",
            fontSize: "0.8em",
            fontWeight: "bold",
            color: "red",
            lineHeight: "1.8em",
          }}
          role="link"
          onClick={() => onDuplicateLabelClicked()}
        >
          {t("Duplicate?")}
        </a>
      )}
    </>
  );

  return (
    <>
      <Input
        size="md"
        label={label}
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        tabIndex={tabIndex}
        loading={isLoading}
      />
    </>
  );
};

export default DocumentNumberField;
